import { useState } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "./ui/card";
import { Badge } from "./ui/badge";
import { Progress } from "./ui/progress";
import { Button } from "./ui/button";
import { LucideIcon, Lock } from "lucide-react";
import { useNavigate } from "react-router-dom";
import PremiumUpgradeDialog from "./PremiumUpgradeDialog";

interface RoadmapTileProps {
  title: string;
  description: string;
  icon: LucideIcon;
  iconColor: string;
  bgGradient: string;
  progress?: {
    completed: number;
    total: number;
    percentage: number;
    modulesCount?: number;
  };
  stats?: {
    label: string;
    value: string | number;
  }[];
  route: string;
  badge?: string;
  isPremium?: boolean;
}

export default function RoadmapTile({
  title,
  description,
  icon: Icon,
  iconColor,
  bgGradient,
  progress,
  stats,
  route,
  badge,
  isPremium,
}: RoadmapTileProps) {
  const navigate = useNavigate();
  const [showUpgradeDialog, setShowUpgradeDialog] = useState(false);

  const handleClick = () => {
    if (isPremium) {
      setShowUpgradeDialog(true);
    } else {
      navigate(route);
    }
  };

  return (
    <>
    <Card
      className="group hover:shadow-lg transition-all duration-300 cursor-pointer overflow-hidden border-2 hover:border-primary/50 flex flex-col h-full"
      onClick={handleClick}
    >
      {/* Gradient Header */}
      <div className={`h-24 ${bgGradient} relative overflow-hidden shrink-0`}>
        <div className="absolute inset-0 bg-black/5" />
        <div className="relative h-full flex items-center justify-between px-6">
          <div className={`p-3 rounded-lg bg-white/90 shadow-md ${iconColor}`}>
            <Icon className="w-8 h-8" />
          </div>
          <div className="flex items-center gap-2">
            {isPremium && (
              <Badge className="bg-gradient-to-r from-yellow-500 to-orange-500 text-white text-xs font-semibold border-0 flex items-center gap-1">
                <Lock className="w-3 h-3" />
                PRO
              </Badge>
            )}
            {badge && (
              <Badge
                variant="secondary"
                className="bg-white/90 text-xs font-semibold"
              >
                {badge}
              </Badge>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex flex-col flex-1">
        <CardHeader>
          <CardTitle className="text-xl group-hover:text-primary transition-colors">
            {title}
          </CardTitle>
          <CardDescription className="text-sm line-clamp-2">
            {description}
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-4 mt-auto">
          {/* Progress & Stats Section */}
          {progress ? (
            <div className="space-y-4">
              {/* Stats Grid */}
              <div className="grid grid-cols-3 gap-2 py-3 border-t border-b">
                <div className="text-center">
                  <div className="text-xl font-bold text-primary">
                    {progress.modulesCount || 0}
                  </div>
                  <div className="text-xs text-muted-foreground">Modules</div>
                </div>
                <div className="text-center">
                  <div className="text-xl font-bold text-primary">
                    {progress.total}
                  </div>
                  <div className="text-xs text-muted-foreground">Lessons</div>
                </div>
                <div className="text-center">
                  <div className="text-xl font-bold text-primary">
                    {progress.completed}
                  </div>
                  <div className="text-xs text-muted-foreground">Done</div>
                </div>
              </div>

              {/* Progress Bar */}
              <div className="space-y-2">
                <div className="flex justify-between text-xs font-medium">
                  <span className="text-muted-foreground">Progress</span>
                  <span className="text-primary">{progress.percentage}%</span>
                </div>
                <Progress value={progress.percentage} className="h-2" />
              </div>
            </div>
          ) : (
            /* Static Stats Section */
            stats &&
            stats.length > 0 && (
              <div className="grid grid-cols-2 gap-3 py-3 border-t border-b">
                {stats.map((stat, index) => (
                  <div key={index} className="text-center">
                    <div className="text-2xl font-bold text-primary">
                      {stat.value}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {stat.label}
                    </div>
                  </div>
                ))}
              </div>
            )
          )}

          {/* Action Button */}
          <Button
            className={`w-full transition-colors ${isPremium ? "bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-white" : "group-hover:bg-primary group-hover:text-primary-foreground"}`}
            variant={isPremium ? "default" : "outline"}
            onClick={(e) => {
              e.stopPropagation();
              handleClick();
            }}
          >
            {isPremium
              ? "Upgrade to Unlock"
              : progress && progress.percentage > 0
                ? "Continue Learning"
                : "Start Learning"}
          </Button>
        </CardContent>
      </div>
    </Card>
    <PremiumUpgradeDialog
      open={showUpgradeDialog}
      onOpenChange={setShowUpgradeDialog}
      feature={title}
    />
    </>
  );
}
